"use client";

import { create } from "zustand";
import { apiUrl } from "@/lib/aicheck/api-base";
import { authService } from "@/services/auth";

interface ExpertFeedbackState {
  /** Set of "${projectId}::${aspectId}" composite keys */
  submitted: Set<string>;
  /** Project IDs for which API status has been loaded (to avoid duplicate fetches) */
  loadedProjects: Set<string>;

  isSubmitted: (projectId: string, aspectId: string) => boolean;
  markSubmitted: (projectId: string, aspectId: string) => void;
  /**
   * Fetch persisted feedback status from the API for the given project.
   * Idempotent — skips if this projectId was already loaded in this session.
   */
  loadStatus: (projectId: string, aspectIds: string[]) => Promise<void>;
}

export const useExpertFeedbackStore = create<ExpertFeedbackState>()(
  (set, get) => ({
    submitted: new Set<string>(),
    loadedProjects: new Set<string>(),

    isSubmitted: (projectId, aspectId) =>
      get().submitted.has(`${projectId}::${aspectId}`),

    markSubmitted: (projectId, aspectId) => {
      set((state) => {
        const next = new Set(state.submitted);
        next.add(`${projectId}::${aspectId}`);
        return { submitted: next };
      });
    },

    loadStatus: async (projectId, aspectIds) => {
      if (aspectIds.length === 0) return;
      // Skip if already loaded for this project
      if (get().loadedProjects.has(projectId)) return;

      try {
        const token = await authService.getIdToken();
        if (!token) throw new Error("未認証です。再度サインインしてください。");
        const params = new URLSearchParams({
          projectId,
          aspectIds: aspectIds.join(","),
        });
        const response = await fetch(
          apiUrl(
            `/api/aicheck/expert-knowledge/feedback/status?${params.toString()}`,
          ),
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!response.ok) return;

        const data = (await response.json()) as {
          ok: boolean;
          feedbackedAspectIds?: string[];
        };
        if (!data.ok || !Array.isArray(data.feedbackedAspectIds)) return;
        const feedbackedIds = data.feedbackedAspectIds;

        set((state) => {
          const next = new Set(state.submitted);
          for (const aspectId of feedbackedIds) {
            next.add(`${projectId}::${aspectId}`);
          }
          const loadedProjects = new Set(state.loadedProjects);
          loadedProjects.add(projectId);
          return { submitted: next, loadedProjects };
        });
      } catch {
        // Best-effort: silent fail — buttons will remain active if status can't be loaded
      }
    },
  }),
);
